/*
 * Copyright (c) 2023-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Injectable} from '@angular/core';
import {ColDef, ColGroupDef} from 'ag-grid-community';

@Injectable({
    providedIn: 'root'
})
export class CommonHelperService {
    private static readonly DAYS_PER_WEEK = 7;
    private static readonly MILLIS_PER_DAY = 24 * 60 * 60 * 1000;

    constructor() { }

    /**
     * Returns true if the supplied column definitions contain a leaf column whose
     * {@code field} matches {@code fieldName}. Used by the grid and detail views to
     * detect whether the active component type carries optional members like
     * {@code ppco} or {@code tierNResilient} without maintaining a hardcoded list of
     * component class names — the column set is built from the backend
     * {@code ObjectDescription}, so this stays in sync automatically when a member is
     * added to or removed from the model. Recurses through {@link ColGroupDef}
     * children so grouped columns are inspected too.
     */
    public static hasFieldInColumns(cols: ReadonlyArray<ColDef | ColGroupDef>, fieldName: string): boolean {
        if (!cols || !fieldName) {
            return false;
        }
        const prefix = fieldName + '.';
        return cols.some(col => {
            if (col && 'children' in col && Array.isArray(col.children)) {
                return CommonHelperService.hasFieldInColumns(col.children, fieldName);
            }
            const field = (col as ColDef)?.field;
            return field === fieldName || field?.startsWith(prefix);
        });
    }

    // This method takes a date as a string and converts it to a date object
    // an optional second param of offset will adjust the date
    static calculateDateWithOffset(date: string, offsetWeeks?: number): Date | null {
        if (!date || date === 'NA' || date === 'null') {
            return null;
        }
        const originalDate = new Date(Date.parse(date));
        originalDate.setHours(0, 0, 0, 0);
        // calculate date with offset of weeks
        if (offsetWeeks) {
            const newDate = new Date(originalDate);
            newDate.setDate(newDate.getDate() + offsetWeeks * this.DAYS_PER_WEEK);
            return newDate;
        }
        return originalDate;
    }

    static calculateWeekOffsetBetweenDates(targetDate: Date, currentDate: Date): number {
        const targetDateOnlyDays = Date.UTC(
            targetDate.getFullYear(),
            targetDate.getMonth(),
            targetDate.getDate()
        ) / this.MILLIS_PER_DAY;
        const currentDateOnlyDays = Date.UTC(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate()
        ) / this.MILLIS_PER_DAY;
        const weekDelta = (targetDateOnlyDays - currentDateOnlyDays) / this.DAYS_PER_WEEK;
        return Math.round(weekDelta);
    }

    static getTimeStampForReport(): string {
        function padDigit(n) {
            return n < 10 ? '0' + n : n;
        }
        const date = new Date();
        return `${date.getFullYear().toString()}${''
        }${padDigit(date.getMonth() + 1)}${''
        }${padDigit(date.getDate())}_${''
        }${padDigit(date.getHours())}${''
        }${padDigit(date.getMinutes())}`;
    }

    static formatDate(value: unknown): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        let date: Date | null = null;
        if (value instanceof Date) {
            date = value;
        } else if (typeof value === 'string' || typeof value === 'number') {
            const parsedDate = new Date(value);
            if (Number.isNaN(parsedDate.getTime())) {
                return '';
            }
            date = parsedDate;

            if (typeof value === 'string') {
                // Preserve historic behavior for string-backed dates from API payloads.
                date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
            }
        }

        if (!date || Number.isNaN(date.getTime())) {
            return '';
        }

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    }

    /**
     * Normalizes mixed date input values into epoch milliseconds for reliable sorting.
     */
    static toEpochMillis(value: unknown): number {
        if (value === null || value === undefined || value === '') {
            return Number.NEGATIVE_INFINITY;
        }
        if (value instanceof Date) {
            return value.getTime();
        }
        if (typeof value === 'number') {
            return value;
        }
        const parsed = Date.parse(String(value));
        return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
    }

    static htmlDecode(input: string): string | null {
        if (input) {
            const doc: Document = new DOMParser().parseFromString(input, 'text/html');
            return doc.documentElement?.textContent || null;
        }
        return input;
    }

    static htmlEncode(input: string | null): string | null {
        if (input) {
            const doc: Document = new DOMParser().parseFromString('<!doctype html><body>' + input, 'text/html');
            return doc.body.innerHTML;
        }
        return input;
    }

    public static getDateString(): string {
        const padDigit = function (n: number) {
            return n < 10 ? '0' + n : n;
        };
        const date = new Date();
        return '_' + date.getFullYear().toString() + padDigit(date.getMonth() + 1) + padDigit(date.getDate()) + '_' +
            padDigit(date.getHours()) + padDigit(date.getMinutes());
    }

    public static downloadFile(blob: Blob, fileName: string): void {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.rel = 'noopener';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }

    /**
     * Get the value of a nested property within an object.
     * @param obj which is the object to be searched
     * @param path The `path` is split into parts using the dot as a delimiter,
     * and each part is used to traverse the properties of the object.
     * @returns {*} The function uses the `reduce` method to iterate over the parts and access the nested properties.
     * If a part does not exist as a property in the object, the function returns `undefined`.
     */
    public static getDeepValue(obj: any, path: string): any {
        if (!path) {
            return obj;
        }
        const parts = path.split('.');
        let sawArraySegment = false;

        const walk = (current: any, index: number): any => {
            if (index >= parts.length) {
                return current;
            }
            if (current === null || current === undefined) {
                return undefined;
            }

            let key = parts[index];
            const isArraySegment = key.endsWith('[]');
            if (isArraySegment) {
                sawArraySegment = true;
                key = key.slice(0, -2);
                const arr = current[key];
                if (!Array.isArray(arr)) {
                    return [];
                }
                return arr.map(item => walk(item, index + 1))
                          .filter(item => item !== undefined);
            }

            return walk(current[key], index + 1);
        };

        const result = walk(obj, 0);
        if (sawArraySegment && result === undefined) {
            return [];
        }
        return result;
    }

    /**
     * Set a value in a nested object using dot notation path.
     * Creates nested objects if they don't exist along the path.
     * @param obj The object to modify
     * @param path The dot-notation path to the property (e.g., 'ppco.priceEstimation')
     * @param value The value to set
     */
    public static setDeepValue(obj: any, path: string, value: any): void {
        if (!path) {
            return;
        }
        const parts = path.split('.');
        const setValueRecursive = (target: any, partIndex: number, nextValue: any): void => {
            if (target == null || partIndex >= parts.length) {
                return;
            }

            const part = parts[partIndex];
            const isArraySegment = part.endsWith('[]');
            const key = isArraySegment ? part.slice(0, -2) : part;
            const isLeaf = partIndex === parts.length - 1;

            if (isArraySegment) {
                const sourceArray = Array.isArray(nextValue) ? nextValue : [];
                if (!Array.isArray(target[key])) {
                    target[key] = [];
                }
                const targetArray = target[key];
                targetArray.length = sourceArray.length;

                if (isLeaf) {
                    for (let i = 0; i < sourceArray.length; i++) {
                        targetArray[i] = sourceArray[i];
                    }
                    return;
                }

                for (let i = 0; i < sourceArray.length; i++) {
                    if (targetArray[i] == null || typeof targetArray[i] !== 'object') {
                        targetArray[i] = {};
                    }
                    setValueRecursive(targetArray[i], partIndex + 1, sourceArray[i]);
                }
                return;
            }

            if (isLeaf) {
                target[key] = nextValue;
                return;
            }

            if (target[key] == null || typeof target[key] !== 'object' || Array.isArray(target[key])) {
                target[key] = {};
            }
            setValueRecursive(target[key], partIndex + 1, nextValue);
        };

        setValueRecursive(obj, 0, value);
    }

    /**
     * Evaluates a format string similar to template literals.
     * Replaces ${variableName} patterns with values from the context.
     * Supports both simple properties and dot-notation paths (e.g., ${val} or ${obj.property}).
     * Also supports simple math operations: ${variable + number}, ${variable - number}, ${variable * number}, ${variable / number}
     * Example: evaluateFormatString("${val} x ${val} mm", { val: 5 }) => "5 x 5 mm"
     * Example: evaluateFormatString("${val.temperatureClass + 25}", { val: { temperatureClass: 100 } }) => "125"
     * @param formatString The format string with ${variableName} patterns
     * @param context An object containing the variable values
     * @returns The formatted string with variables replaced
     */
    public static evaluateFormatString(formatString: string, context: Record<string, any>): string {
        if (!formatString) {
            return '';
        }

        // Pattern to match math expressions: variable operator number (e.g., "val.temp + 25", "val.num - 10")
        const mathExpressionPattern = /^(.+?)\s*([+\-*/])\s*([+-]?\d+(?:\.\d+)?)$/;

        // Replace ${variableName} patterns with actual values
        return formatString.replace(/\$\{([^}]+)\}/g, (match, expression) => {
            const trimmedExpr = expression.trim();

            // Check if this is a math expression (e.g., "val.temp + 25")
            const mathMatch = trimmedExpr.match(mathExpressionPattern);
            if (mathMatch) {
                // It's a math expression: variable operator number
                const variableName = mathMatch[1].trim();
                const operator = mathMatch[2].trim();
                const numberStr = mathMatch[3].trim();

                // Get the variable value
                const variableValue = this.getDeepValue(context, variableName);
                if (variableValue === undefined || variableValue === null) {
                    return '';
                }

                // Perform the math operation
                return this.evaluateMathExpression(variableValue, operator, numberStr);
            } else {
                // Regular variable reference (no math)
                const value = this.getDeepValue(context, trimmedExpr);

                // Handle undefined/null values
                if (value === undefined || value === null) {
                    return '';
                }

                // Format numeric values consistently - remove decimal point for whole numbers
                if (typeof value === 'number') {
                    if (value === Math.floor(value)) {
                        return String(Math.floor(value));
                    }
                    return String(value);
                }

                // Return the value as a string
                return String(value);
            }
        });
    }

    /**
     * Evaluates a simple math expression: variableValue operator number.
     * Supports: +, -, *, /
     * @param variableValue The value of the variable (must be numeric)
     * @param operator The math operator (+, -, *, /)
     * @param numberStr The number as a string
     * @returns The result as a string, or empty string if evaluation fails
     */
    private static evaluateMathExpression(variableValue: any, operator: string, numberStr: string): string {
        try {
            // Convert variable value to number
            const varNum = this.convertToNumber(variableValue);
            const num = parseFloat(numberStr);

            if (isNaN(varNum) || isNaN(num)) {
                return '';
            }

            let result: number;
            switch (operator) {
                case '+':
                    result = varNum + num;
                    break;
                case '-':
                    result = varNum - num;
                    break;
                case '*':
                    result = varNum * num;
                    break;
                case '/':
                    if (num === 0) {
                        console.warn('Division by zero in format string expression');
                        return '';
                    }
                    result = varNum / num;
                    break;
                default:
                    console.warn(`Unsupported operator '${operator}' in format string expression`);
                    return '';
            }

            // Format the result - remove decimal point if it's a whole number
            if (result === Math.floor(result)) {
                return String(Math.floor(result));
            }
            return String(result);
        } catch (e) {
            console.debug(`Error evaluating math expression: ${e}`);
            return '';
        }
    }

    /**
     * Converts a value to a number.
     * Handles Number types, numeric strings, and other common numeric formats.
     * @param value The value to convert
     * @returns The number value, or NaN if conversion fails
     */
    private static convertToNumber(value: any): number {
        if (value === null || value === undefined) {
            return NaN;
        }

        if (typeof value === 'number') {
            return value;
        }

        if (typeof value === 'string') {
            return parseFloat(value);
        }

        // Try to convert via string representation
        return parseFloat(String(value));
    }
}
